import type { DocumentDTO } from "@zplanem/responses"
import { useState } from "react"
import { ApiError } from "../../../lib/api-client"
import { formatBytes } from "../../../lib/format-bytes"
import {
	type DocumentScope,
	useDeleteDocument,
	useDocumentReviewRealtime,
	useDownloadDocument,
} from "../../hooks/use-documents"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"
import { ConfirmDeleteDialog } from "../confirm-delete-dialog"
import { AiReviewBadge } from "./ai-review-badge"

function FileKindIcon({ kind }: { kind: string }) {
	return (
		<span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background border border-border text-[9px] font-bold uppercase text-muted-foreground">
			{kind}
		</span>
	)
}

export function DocumentList({
	documents,
	scope,
	onOpenGrants,
	onEdit,
	onReviewAiClick,
	error,
	scopeLabelFor,
}: {
	documents: DocumentDTO[]
	scope: DocumentScope
	onOpenGrants: (doc: DocumentDTO) => void
	onEdit?: (doc: DocumentDTO) => void
	onReviewAiClick?: (doc: DocumentDTO) => void
	error?: unknown
	scopeLabelFor?: (doc: DocumentDTO) => string | null
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const td = t.projects.documents
	const remove = useDeleteDocument(scope)
	const download = useDownloadDocument(scope.propertyId)
	// One subscription for the whole list keeps every AI-review badge fresh.
	useDocumentReviewRealtime(scope.propertyId)
	const [deleteError, setDeleteError] = useState<string | null>(null)
	const [downloadError, setDownloadError] = useState<string | null>(null)
	// ZPL-334: deletion is irreversible, so the trash icon only ARMS this —
	// the actual mutation runs from the confirm dialog.
	const [confirmingDelete, setConfirmingDelete] = useState<DocumentDTO | null>(
		null,
	)

	function openDeleteConfirm(doc: DocumentDTO) {
		// A previous attempt's parked error must not leak into this dialog.
		setDeleteError(null)
		setConfirmingDelete(doc)
	}

	function closeDeleteConfirm() {
		setConfirmingDelete(null)
		setDeleteError(null)
	}

	async function handleDelete(docId: string) {
		setDeleteError(null)
		try {
			await remove.mutateAsync(docId)
			closeDeleteConfirm()
		} catch (err) {
			// Keep the dialog open — the error renders inside it for a retry.
			setDeleteError(err instanceof ApiError ? err.message : td.errorLoad)
		}
	}

	async function handleOpen(doc: DocumentDTO) {
		setDownloadError(null)
		// Open a blank tab synchronously inside the user-gesture handler so the
		// later (post-await) navigation isn't treated as a popup and blocked.
		// Do NOT pass "noopener" here — that makes window.open() return null, which
		// would strand this blank tab and send the file to the CURRENT tab. We sever
		// the opener manually instead, keeping a usable handle to navigate.
		const win = window.open("", "_blank")
		if (win) win.opener = null
		try {
			const url = await download.mutateAsync({ documentId: doc.id })
			if (win) {
				win.location.href = url
			} else {
				// Popup blocked — don't hijack the current tab; ask the user to allow it.
				setDownloadError(td.errorDownloadBlocked)
			}
		} catch (err) {
			win?.close()
			setDownloadError(err instanceof ApiError ? err.message : td.errorDownload)
		}
	}

	async function handleDownload(doc: DocumentDTO) {
		setDownloadError(null)
		try {
			const url = await download.mutateAsync({
				documentId: doc.id,
				disposition: "attachment",
			})
			// Trigger a real save-to-disk via a temporary anchor. Downloads are
			// never popup-blocked (no new window opened), and the attachment
			// Content-Disposition in the signed URL makes the browser save the file
			// even though the URL is cross-origin (the HTML `download` attr is
			// ignored cross-origin but the response header is not).
			const a = document.createElement("a")
			a.href = url
			a.download = doc.originalFilename ?? doc.title ?? "document"
			a.rel = "noopener"
			document.body.appendChild(a)
			a.click()
			a.remove()
		} catch (err) {
			setDownloadError(err instanceof ApiError ? err.message : td.errorDownload)
		}
	}

	if (error) {
		return (
			<div role="alert" className="text-sm text-destructive">
				{error instanceof ApiError ? (error as ApiError).message : td.errorLoad}
			</div>
		)
	}

	return (
		<div className="space-y-2">
			{downloadError ? (
				<div role="alert" className="text-sm text-destructive">
					{downloadError}
				</div>
			) : null}
			{documents.map((doc) => {
				const innerContent = (
					<>
						<FileKindIcon kind={doc.kind} />
						<div className="min-w-0 flex-1">
							<div className="flex min-w-0 items-center gap-1.5">
								<p
									className={[
										"truncate text-sm font-medium text-foreground",
										doc.canDownload ? "group-hover:underline" : "",
									].join(" ")}
								>
									{doc.title}
								</p>
								{scopeLabelFor
									? (() => {
											const label = scopeLabelFor(doc)
											return label ? (
												<span className="shrink-0 rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
													{label}
												</span>
											) : null
										})()
									: null}
							</div>
							<p className="text-xs text-muted-foreground/80">
								{formatBytes(doc.sizeBytes)}
								{doc.originalFilename ? ` · ${doc.originalFilename}` : ""}
							</p>
						</div>
					</>
				)

				return (
					<div
						key={doc.id}
						className="flex min-w-0 items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2"
					>
						{doc.canDownload ? (
							<button
								type="button"
								aria-label={td.open}
								onClick={() => void handleOpen(doc)}
								className="flex min-w-0 flex-1 items-center gap-3 group text-left"
							>
								{innerContent}
							</button>
						) : (
							<div className="flex min-w-0 flex-1 items-center gap-3">
								{innerContent}
							</div>
						)}
						<div className="flex shrink-0 items-center gap-1">
							{onReviewAiClick ? (
								<AiReviewBadge
									propertyId={scope.propertyId}
									document={doc}
									onOpen={() => onReviewAiClick(doc)}
								/>
							) : null}
							{doc.canDownload ? (
								<button
									type="button"
									aria-label={td.download}
									disabled={download.isPending}
									onClick={() => void handleDownload(doc)}
									className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors disabled:opacity-50"
								>
									<DownloadIcon />
								</button>
							) : null}
							{doc.canManageGrants && onEdit ? (
								<button
									type="button"
									aria-label={td.editCta}
									onClick={() => onEdit(doc)}
									className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
								>
									<PencilIcon />
								</button>
							) : null}
							{doc.canManageGrants ? (
								<button
									type="button"
									aria-label={
										doc.grantCount > 0
											? `${td.grantsCta} (${doc.grantCount})`
											: td.grantsCta
									}
									onClick={() => onOpenGrants(doc)}
									className="relative inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
								>
									<ShareIcon />
									{doc.grantCount > 0 ? (
										// ZPL-332: how many people have access. Bottom-right,
										// mirroring the bell's unread badge; decorative — the
										// count lives in the button's aria-label.
										<span
											aria-hidden="true"
											className="absolute -bottom-0.5 -right-0.5 min-w-3.5 h-3.5 px-0.5 rounded-full bg-primary/70 text-primary-foreground text-[9px] leading-[14px] text-center font-semibold"
										>
											{doc.grantCount > 99 ? "99+" : doc.grantCount}
										</span>
									) : null}
								</button>
							) : null}
							{doc.canManageGrants ? (
								<button
									type="button"
									aria-label={td.delete}
									disabled={remove.isPending}
									onClick={() => openDeleteConfirm(doc)}
									className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
								>
									<TrashIcon />
								</button>
							) : null}
						</div>
					</div>
				)
			})}
			{confirmingDelete ? (
				<ConfirmDeleteDialog
					title={td.deleteTitle}
					// Replacer fn: a plain-string second arg would expand $-patterns
					// ($&, $') from the user-controlled title into the template.
					body={td.deleteBody.replace("{title}", () => confirmingDelete.title)}
					confirmLabel={td.deleteConfirm}
					cancelLabel={td.cancel}
					pending={remove.isPending}
					error={deleteError}
					onCancel={closeDeleteConfirm}
					onConfirm={() => void handleDelete(confirmingDelete.id)}
				/>
			) : null}
		</div>
	)
}

function PencilIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="w-4 h-4"
			aria-hidden="true"
		>
			<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
			<path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5Z" />
		</svg>
	)
}

function ShareIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="w-4 h-4"
			aria-hidden="true"
		>
			<circle cx="18" cy="5" r="3" />
			<circle cx="6" cy="12" r="3" />
			<circle cx="18" cy="19" r="3" />
			<path d="m8.59 13.51 6.83 3.98M15.41 6.51l-6.82 3.98" />
		</svg>
	)
}

function TrashIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="w-4 h-4"
			aria-hidden="true"
		>
			<path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
		</svg>
	)
}

function DownloadIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="w-4 h-4"
			aria-hidden="true"
		>
			<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
			<polyline points="7 10 12 15 17 10" />
			<line x1="12" y1="15" x2="12" y2="3" />
		</svg>
	)
}
